import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import { useNavigate } from "react-router-dom";
import ContactApiService from "../services/ContactApiService";
import LeadService from "../services/LeadService";
import {
  flattenNavSearchItems,
  filterNavSearchItems,
  navSections,
} from "../config/navSections";

const PAGE_ICON = TravelExploreOutlinedIcon;

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

async function fetchRemoteResults(query) {
  if (!query || query.trim().length < 2) {
    return [];
  }
  const search = query.trim();
  const [contactsRes, leadsRes] = await Promise.allSettled([
    ContactApiService.getContactsDetails({ search, page: 1, limit: 5 }),
    LeadService.getLeads({ search, page: 1, limit: 5 }),
  ]);

  const results = [];

  if (contactsRes.status === "fulfilled" && contactsRes.value?.success) {
    (contactsRes.value.data?.contacts || []).forEach((contact) => {
      const name = contact.getName?.() || contact.name || "Contact";
      const phone = contact.getPhoneNumber?.() || contact.phone_number || "";
      const id = contact.getId?.() || contact.id;
      results.push({
        id: `contact-${id}`,
        type: "contact",
        label: name,
        subtitle: phone || "Contact",
        path: `/contacts?q=${encodeURIComponent(search)}`,
      });
    });
  }

  if (leadsRes.status === "fulfilled") {
    (leadsRes.value?.leads || []).forEach((lead) => {
      results.push({
        id: `lead-${lead.id}`,
        type: "lead",
        label: lead.title || lead.name || "Lead",
        subtitle: lead.stage_name || lead.status || "Lead",
        path: `/leads/list?q=${encodeURIComponent(search)}`,
      });
    });
  }

  return results;
}

export default function GlobalSearchBar({ compact = false }) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remoteOptions, setRemoteOptions] = useState([]);
  const debouncedQuery = useDebouncedValue(inputValue, 300);

  const navItems = useMemo(() => flattenNavSearchItems(navSections), []);

  useEffect(() => {
    let cancelled = false;
    if (!debouncedQuery.trim() || debouncedQuery.trim().length < 2) {
      setRemoteOptions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchRemoteResults(debouncedQuery)
      .then((items) => {
        if (!cancelled) setRemoteOptions(items);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const options = useMemo(() => {
    const q = inputValue.trim();
    if (!q) return [];
    const pages = filterNavSearchItems(navItems, q);
    const merged = [...pages];
    remoteOptions.forEach((item) => {
      if (!merged.some((m) => m.id === item.id)) merged.push(item);
    });
    return merged.slice(0, 12);
  }, [inputValue, navItems, remoteOptions]);

  const handleSelect = useCallback((_event, option) => {
    if (!option?.path) return;
    navigate(option.path);
    setInputValue("");
    setOpen(false);
  }, [navigate]);

  const getOptionIcon = (type) => {
    if (type === "contact") return ContactsOutlinedIcon;
    if (type === "lead") return FlagOutlinedIcon;
    return PAGE_ICON;
  };

  return (
    <Autocomplete
      freeSolo
      open={open && inputValue.trim().length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      inputValue={inputValue}
      onInputChange={(_e, value) => setInputValue(value)}
      onChange={handleSelect}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      slotProps={{
        popper: { sx: { zIndex: (t) => t.zIndex.modal } },
        paper: {
          sx: {
            mt: 0.75,
            borderRadius: 2.5,
            border: "1px solid var(--theme-app-bar-border, #e5e7eb)",
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
          },
        },
      }}
      renderOption={(props, option) => {
        const Icon = getOptionIcon(option.type);
        return (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: "rgba(37, 99, 235, 0.08)",
                color: "#2563eb",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                {option.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b" }} noWrap>
                {option.subtitle}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={compact
            ? "Search…"
            : "Search contacts, leads, chats, pages…"}
          size="small"
          aria-label="Global search"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "#f8fafc",
              fontSize: 14,
              pl: 0.5,
              transition: "background-color 0.2s ease, box-shadow 0.2s ease",
              "& fieldset": {
                borderColor: "#c5c7c9",
              },
              "&:hover fieldset": {
                borderColor: "#3b3e41",
              },
              "&.Mui-focused": {
                bgcolor: "#ffffff",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1565c0",
              },
            },
          }}
        />
      )}
      sx={{
        width: "100%",
        maxWidth: compact ? "100%" : 250,
      }}
    />
  );
}
